import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useState } from 'react';

import STORAGE_KEYS from '@/configs/local-storage-keys';
import queryKeys from '@/configs/react-query-keys';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import service from '@/services/index';
import useTaxonomyStore from '@/stores/TaxonomyStore';

export enum TopicTreeSortBy {
  TITLE = 1,
}

type TaxonomyContextT = {
  taxonomyAlias: string | null;
  sortBy: TopicTreeSortBy;
  order: OrderStatusT;
  setOrderState: (sortBy: TopicTreeSortBy, order: OrderStatusT) => void;
  fetchTopics: () => void;
  refetchTopicDetail: (topicAlias: string) => void;
  refetchTaxonomy: () => void;
  taxonomy?: TaxonomyT;
  isFetched: boolean;
  topicListFetched: boolean;
};

const TaxonomyContext = createContext<TaxonomyContextT>({
  taxonomyAlias: null,
  sortBy: TopicTreeSortBy.TITLE,
  order: 'desc',
  setOrderState: () => {},
  fetchTopics: () => {},
  refetchTopicDetail: () => {},
  refetchTaxonomy: () => {},
  isFetched: false,
  topicListFetched: false,
});

type ProviderProps = {
  taxonomyAlias: string;
  children: ReactNode;
};

const TaxonomyProvider = (props: ProviderProps) => {
  const queryClient = useQueryClient();
  const fetchTaxnomyTopics = useTaxonomyStore(
    (state) => state.fetchTaxonomyTopics,
  );
  const [state, setState] = useWorkspaceStorage<
    Pick<TaxonomyContextT, 'sortBy' | 'order'>
  >(`${STORAGE_KEYS.TAXONOMY_TOPIC_TREE_STATE}_${props.taxonomyAlias}`, {
    sortBy: TopicTreeSortBy.TITLE,
    order: 'desc',
  });

  const [topicListFetched, setTopicListFetched] = useState(false);

  const fetchTopics = useCallback(async () => {
    const res = await fetchTaxnomyTopics(props.taxonomyAlias);
    setTopicListFetched(true);
    return res;
  }, [fetchTaxnomyTopics, props.taxonomyAlias]);

  const taxonomyQuery = useQuery({
    queryFn: () => {
      return service.taxonomy.fetchTaxonomyItem(props.taxonomyAlias);
    },
    queryKey: [...queryKeys.taxonomy.TAXONOMY_ITEM, props.taxonomyAlias],
  });

  const refetchTopicDetail = (topicAlias: string) => {
    queryClient.invalidateQueries({ queryKey: [
      ...queryKeys.taxonomy.TOPIC_DETAIL,
      topicAlias,
    ] });
  };

  return (
    <TaxonomyContext.Provider
      value={{
        taxonomyAlias: props.taxonomyAlias,
        sortBy: state.sortBy,
        order: state.order,
        setOrderState(sortBy, order) {
          setState({
            ...state,
            sortBy,
            order,
          });
        },
        taxonomy: taxonomyQuery.data,
        isFetched: taxonomyQuery.isFetched,
        fetchTopics,
        refetchTopicDetail,
        refetchTaxonomy: taxonomyQuery.refetch,
        topicListFetched: topicListFetched,
      }}
    >
      {props.children}
    </TaxonomyContext.Provider>
  );
};

export { TaxonomyProvider };

export default TaxonomyContext;
